import React, { useState, useEffect } from 'react';
import axios from 'axios';
import io from 'socket.io-client';
import QRCode from 'qrcode.react';
import './App.css';

// Environment-based configuration
const isDevelopment = process.env.NODE_ENV === 'development' || 
                     window.location.hostname === 'localhost' || 
                     window.location.hostname === '127.0.0.1';

// Production URLs
const PROD_API_URL = 'https://server.himconerp.com/api';
const PROD_SOCKET_URL = 'https://server.himconerp.com';

// Development URLs
const DEV_API_URL = 'http://localhost:5000/api';
const DEV_SOCKET_URL = 'http://localhost:5000';

// Use appropriate URLs based on environment
const API_URL = isDevelopment ? DEV_API_URL : PROD_API_URL;
const SOCKET_URL = isDevelopment ? DEV_SOCKET_URL : PROD_SOCKET_URL;

// Socket.io configuration
const socket = io(SOCKET_URL, {
  transports: ['websocket', 'polling'],
  reconnection: true,
  reconnectionDelay: 1000,
  reconnectionAttempts: 5,
  secure: !isDevelopment // Use secure connection in production (HTTPS)
});
function App() {
  const [qrCode, setQrCode] = useState('');
  const [status, setStatus] = useState('disconnected');
  const [isReady, setIsReady] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [chats, setChats] = useState([]);
  const [showChats, setShowChats] = useState(false);
  const [bulkMode, setBulkMode] = useState(false);
  const [bulkNumbers, setBulkNumbers] = useState('');
  const [bulkSending, setBulkSending] = useState(false);
  const [bulkResults, setBulkResults] = useState(null);
  const [socketConnected, setSocketConnected] = useState(false);

  useEffect(() => {
    // Socket connection events
    socket.on('connect', () => {
      console.log('Socket connected:', socket.id);
      setSocketConnected(true);
      setStatus('connecting');
    });

    socket.on('disconnect', () => {
      console.log('Socket disconnected');
      setSocketConnected(false);
      setStatus('disconnected');
      setIsReady(false);
    });

    socket.on('connect_error', (error) => {
      console.error('Socket connection error:', error);
      setSocketConnected(false);
      setStatus('connection_error');
    });

    // Socket event listeners
    socket.on('qr', (qr) => {
      console.log('QR Code received in frontend');
      setQrCode(qr);
      setStatus('scanning');
    });

    socket.on('ready', () => {
      console.log('WhatsApp ready - connected');
      setStatus('connected');
      setIsReady(true);
      setQrCode('');
    });

    socket.on('authenticated', () => {
      console.log('WhatsApp authenticated');
      setStatus('authenticating');
    });

    socket.on('auth_failure', () => {
      console.error('WhatsApp authentication failed');
      setStatus('auth_failed');
      setIsReady(false);
    });

    socket.on('disconnected', () => {
      console.log('WhatsApp disconnected');
      setStatus('disconnected');
      setIsReady(false);
    });

    // Check initial status
    checkStatus();

    return () => {
      socket.off('connect');
      socket.off('disconnect');
      socket.off('connect_error');
      socket.off('qr');
      socket.off('ready');
      socket.off('authenticated');
      socket.off('auth_failure');
      socket.off('disconnected');
    };
  }, []);

  const checkStatus = async () => {
    try {
      const response = await axios.get(`${API_URL}/status`);
      setIsReady(response.data.isReady);
      setStatus(response.data.isReady ? 'connected' : 'disconnected');
    } catch (error) {
      console.error('Error checking status:', error);
    }
  };

  const handleDisconnect = async () => {
    if (!window.confirm('Are you sure you want to disconnect WhatsApp? You will need to scan QR code again to reconnect.')) {
      return;
    }

    try {
      const response = await axios.post(`${API_URL}/disconnect`);
      if (response.data.success) {
        setIsReady(false);
        setStatus('disconnected');
        setQrCode('');
        setChats([]);
        setShowChats(false);
        alert('WhatsApp disconnected successfully. Click "Connect" to reconnect and scan QR code.');
      }
    } catch (error) {
      console.error('Error disconnecting:', error);
      alert('Failed to disconnect: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleReconnect = async () => {
    try {
      const response = await axios.post(`${API_URL}/reconnect`);
      if (response.data.success) {
        setStatus('connecting');
        setIsReady(false);
        setQrCode('');
        setChats([]);
        setShowChats(false);
        // Don't show alert, just wait for QR code
        console.log('Reconnection initiated. Waiting for QR code...');
      }
    } catch (error) {
      console.error('Error reconnecting:', error);
      alert('Failed to reconnect: ' + (error.response?.data?.error || error.message));
    }
  };

  const fetchChats = async () => {
    try {
      const response = await axios.get(`${API_URL}/chats`);
      setChats(response.data.chats);
      setShowChats(true);
    } catch (error) {
      console.error('Error fetching chats:', error);
      alert('Failed to fetch chats: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    
    if (!phoneNumber || !message) {
      alert('Please enter both phone number and message');
      return;
    }

    setSending(true);
    try {
      await axios.post(`${API_URL}/send-message`, {
        number: phoneNumber,
        message: message
      });

      alert('Message sent successfully!');
      setMessage('');
      setPhoneNumber('');
    } catch (error) {
      console.error('Error sending message:', error);
      alert('Failed to send message: ' + (error.response?.data?.error || error.message));
    } finally {
      setSending(false);
    }
  };

  const handleBulkSend = async (e) => {
    e.preventDefault();
    
    if (!bulkNumbers || !message) {
      alert('Please enter phone numbers and message');
      return;
    }

    // Parse numbers (split by comma, newline, or space)
    const numbersArray = bulkNumbers
      .split(/[,\n\r]+/)
      .map(num => num.trim())
      .filter(num => num.length > 0);

    if (numbersArray.length === 0) {
      alert('Please enter at least one phone number');
      return;
    }

    setBulkSending(true);
    setBulkResults(null);
    
    try {
      const response = await axios.post(`${API_URL}/send-bulk-messages`, {
        numbers: numbersArray,
        message: message
      });

      setBulkResults(response.data);
      
      const successMsg = `Messages sent!\nSuccessful: ${response.data.successful}/${response.data.total}\nFailed: ${response.data.failed}/${response.data.total}`;
      alert(successMsg);
      
      if (response.data.failed === 0) {
        setBulkNumbers('');
        setMessage('');
      }
    } catch (error) {
      console.error('Error sending bulk messages:', error);
      alert('Failed to send bulk messages: ' + (error.response?.data?.error || error.message));
    } finally {
      setBulkSending(false);
    }
  };

  const getStatusColor = () => {
    switch (status) {
      case 'connected':
        return '#4caf50';
      case 'scanning':
      case 'authenticating':
        return '#ff9800';
      case 'auth_failed':
        return '#f44336';
      default:
        return '#9e9e9e';
    }
  };

  const getStatusText = () => {
    switch (status) {
      case 'connected':
        return 'Connected';
      case 'scanning':
        return 'Scan QR Code';
      case 'authenticating':
        return 'Authenticating...';
      case 'auth_failed':
        return 'Authentication Failed';
      default:
        return 'Disconnected';
    }
  };

  return (
    <div className="App">
      <div className="container">
        <header className="header">
          <h1>WhatsApp Web App</h1>
          <div className="status-indicator">
            <span 
              className="status-dot" 
              style={{ backgroundColor: getStatusColor() }}
            ></span>
            <span className="status-text">{getStatusText()}</span>
            <span className="socket-status" style={{ 
              marginLeft: '10px', 
              fontSize: '0.85em', 
              color: socketConnected ? '#4caf50' : '#f44336' 
            }}>
              {socketConnected ? '🔌 Connected' : '🔌 Disconnected'}
            </span>
            <div style={{ marginLeft: '20px', display: 'inline-flex', gap: '10px' }}>
              {isReady ? (
                <button 
                  onClick={handleDisconnect}
                  className="btn btn-danger"
                  style={{ 
                    padding: '5px 15px', 
                    fontSize: '0.9em',
                    backgroundColor: '#f44336',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer'
                  }}
                >
                  Disconnect
                </button>
              ) : (
                <button 
                  onClick={handleReconnect}
                  className="btn btn-success"
                  style={{ 
                    padding: '5px 15px', 
                    fontSize: '0.9em',
                    backgroundColor: '#4caf50',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer'
                  }}
                >
                  Connect
                </button>
              )}
            </div>
          </div>
        </header>

        {qrCode ? (
          <div className="qr-section">
            <h2>Scan QR Code with WhatsApp</h2>
            <div className="qr-container">
              <QRCode value={qrCode} size={256} />
            </div>
            <p className="qr-instructions">
              Open WhatsApp on your phone → Settings → Linked Devices → Link a Device
            </p>
            <p style={{ fontSize: '0.85em', color: '#666', marginTop: '10px' }}>
              QR Code received: {qrCode.substring(0, 20)}...
            </p>
          </div>
        ) : (
          !isReady && (
            <div className="waiting-section">
              <p>Waiting for QR Code...</p>
              <p style={{ fontSize: '0.85em', color: '#666', marginTop: '10px' }}>
                Socket Status: {socketConnected ? '✅ Connected' : '❌ Disconnected'}
              </p>
            </div>
          )
        )}

        {isReady && (
          <div className="main-content">
            <div className="action-buttons">
              <button onClick={fetchChats} className="btn btn-secondary">
                {showChats ? 'Hide Chats' : 'Load Chats'}
              </button>
              <button 
                onClick={() => {
                  setBulkMode(!bulkMode);
                  setBulkResults(null);
                }} 
                className="btn btn-secondary"
              >
                {bulkMode ? 'Single Message' : 'Bulk Message'}
              </button>
            </div>

            {showChats && chats.length > 0 && (
              <div className="chats-section">
                <h3>Your Chats ({chats.length})</h3>
                <div className="chats-list">
                  {chats.slice(0, 10).map((chat) => {
                    // Extract phone number from chat ID
                    const phoneFromId = chat.id.split('@')[0];
                    return (
                      <div 
                        key={chat.id} 
                        className="chat-item"
                        onClick={() => {
                          setPhoneNumber(phoneFromId);
                          setShowChats(false); // Close chat list after selection
                        }}
                      >
                        <div className="chat-name">{chat.name || phoneFromId}</div>
                        {chat.unreadCount > 0 && (
                          <span className="unread-badge">{chat.unreadCount}</span>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {!bulkMode ? (
              <form onSubmit={handleSendMessage} className="message-form">
                <h3>Send Single Message</h3>
                
                <div className="form-group">
                  <label htmlFor="phoneNumber">Phone Number</label>
                  <input
                    type="text"
                    id="phoneNumber"
                    value={phoneNumber}
                    onChange={(e) => setPhoneNumber(e.target.value)}
                    placeholder="e.g., 919876543210 (with country code)"
                    required
                  />
                  <small>
                    <strong>Tip:</strong> Use numbers from your chat list above for best results. 
                    Format: CountryCode + Number (e.g., 919876543210 for India, no + sign, no spaces)
                  </small>
                </div>

                <div className="form-group">
                  <label htmlFor="message">Message</label>
                  <textarea
                    id="message"
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    placeholder="Type your message here..."
                    rows="5"
                    required
                  />
                </div>

                <button 
                  type="submit" 
                  className="btn btn-primary"
                  disabled={sending}
                >
                  {sending ? 'Sending...' : 'Send Message'}
                </button>
              </form>
            ) : (
              <form onSubmit={handleBulkSend} className="message-form">
                <h3>Send Bulk Messages</h3>
                
                <div className="form-group">
                  <label htmlFor="bulkNumbers">Phone Numbers (one per line or comma-separated)</label>
                  <textarea
                    id="bulkNumbers"
                    value={bulkNumbers}
                    onChange={(e) => setBulkNumbers(e.target.value)}
                    placeholder="919876543210&#10;919876543211&#10;919876543212"
                    rows="6"
                    required
                  />
                  <small>
                    Enter multiple phone numbers separated by commas or new lines.
                    Format: CountryCode + Number (e.g., 919876543210 for India)
                  </small>
                </div>

                <div className="form-group">
                  <label htmlFor="bulkMessage">Message</label>
                  <textarea
                    id="bulkMessage"
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    placeholder="Type your message here (will be sent to all numbers)..."
                    rows="5"
                    required
                  />
                </div>

                <button 
                  type="submit" 
                  className="btn btn-primary"
                  disabled={bulkSending}
                >
                  {bulkSending ? `Sending... (${bulkResults ? `${bulkResults.successful}/${bulkResults.total}` : '0'})` : 'Send to All Numbers'}
                </button>

                {bulkResults && (
                  <div className="bulk-results">
                    <h4>Results:</h4>
                    <div className="results-summary">
                      <div className="result-item success">
                        ✅ Successful: {bulkResults.successful}/{bulkResults.total}
                      </div>
                      <div className="result-item error">
                        ❌ Failed: {bulkResults.failed}/{bulkResults.total}
                      </div>
                    </div>
                    {bulkResults.failed > 0 && (
                      <div className="failed-numbers">
                        <strong>Failed Numbers:</strong>
                        <ul>
                          {bulkResults.results
                            .filter(r => !r.success)
                            .map((r, idx) => (
                              <li key={idx}>{r.number}: {r.error}</li>
                            ))}
                        </ul>
                      </div>
                    )}
                  </div>
                )}
              </form>
            )}
          </div>
        )}

        {!isReady && !qrCode && (
          <div className="waiting-section">
            <p>Waiting for WhatsApp connection...</p>
          </div>
        )}
      </div>
    </div>
  );
}

export default App;
